import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DateUtil } from '../../../DateUtil';
import { TimelineBoApproval } from '../../../model/divestment/TimelineBoApproval';
import {
  OwnershipMilestoneId,
  OwnershipMilestoneIdIsBoApproval,
  OwnershipMilestoneIdIsNboApproval,
  OwnershipMilestoneIdToDescription,
  OwnershipMilestoneIdToTitle,
} from '../../../model/divestment/OwnershipMilestoneId';
import { TimelineNboApproval } from '../../../model/divestment/TimelineNboApproval';
import {
  TimelineNboApprovalToLabel,
  TimelineNboApprovalToTooltip,
  TimelineBoApprovalToLabel,
  TimelineBoApprovalToTooltip,
  TimelineFormCtrlSuffix,
} from '../DivestmentTabData';
import { ValidatorUtil } from 'src/app/shared/ValidatorUtil';

@Component({
  selector: 'digires-divestment-milestone',
  templateUrl: './divestment-milestone.component.html',
  styleUrls: ['./divestment-milestone.component.scss'],
})
export class DivestmentMilestoneComponent implements OnChanges {
  public readonly ValidatorUtil = ValidatorUtil;
  public readonly nboApprovalToLabel = TimelineNboApprovalToLabel;
  public readonly nboApprovalToTooltip = TimelineNboApprovalToTooltip;
  public readonly boApprovalToLabel = TimelineBoApprovalToLabel;
  public readonly boApprovalToTooltip = TimelineBoApprovalToTooltip;

  @Output() onChange: EventEmitter<void> = new EventEmitter<void>();
  @Input() readonly: boolean;
  @Input() formGroup: FormGroup;
  @Input() milestoneId: OwnershipMilestoneId;
  @Input() activeMilestoneId: OwnershipMilestoneId;
  allNboApprovalOptions = Object.keys(TimelineNboApproval) as TimelineNboApproval[];
  allBoApprovalOptions = Object.keys(TimelineBoApproval) as TimelineBoApproval[];

  title: string;
  description: string;
  isNboApproval: boolean;
  isBoApproval: boolean;

  ngOnChanges() {
    this.title = OwnershipMilestoneIdToTitle[this.milestoneId];
    this.description = OwnershipMilestoneIdToDescription[this.milestoneId];
    this.isNboApproval = OwnershipMilestoneIdIsNboApproval[this.milestoneId];
    this.isBoApproval = OwnershipMilestoneIdIsBoApproval[this.milestoneId];
  }

  emitEvent() {
    this.onChange.emit();
  }

  isDone(): boolean {
    return this.isComplete() || this.isNotAvailable();
  }

  isActive(): boolean {
    return this.activeMilestoneId === this.milestoneId;
  }

  hasNoDate(): boolean {
    return !this.formGroup.value[this.getFormControlNameDate()];
  }

  getCompleteCheckboxReadOnlyReason(): string {
    if (!(this.isNotAvailable() || this.readonly) && this.hasNoDate()) {
      return 'Please provide a Forecast Date before completion';
    }
    return undefined;
  }

  isComplete(): boolean {
    return !!this.formGroup.value[this.getFormControlNameComplete()];
  }

  isCompleteDirty(): boolean {
    return this.formGroup.get(this.getFormControlNameComplete()).dirty;
  }

  isNotAvailable(): boolean {
    return !!this.formGroup.value[this.getFormControlNameNa()];
  }

  isNotAvailableDirty(): boolean {
    return this.formGroup.get(this.getFormControlNameNa()).dirty;
  }

  getDate(): Date {
    const value = this.formGroup.value[this.getFormControlNameDate()];
    return DateUtil.anyToDate(value);
  }

  getFormControlNameNa(): string {
    return this.milestoneId + TimelineFormCtrlSuffix.na;
  }

  getFormControlNameComplete(): string {
    return this.milestoneId + TimelineFormCtrlSuffix.complete;
  }

  getFormControlNameDate(): string {
    return this.milestoneId + TimelineFormCtrlSuffix.date;
  }

  getPlaceholderDate(): string {
    if (this.isComplete()) {
      return 'Completion';
    }
    return 'Forecast';
  }

  getEndOfProcessCheckboxLabel(): string {
    if (this.milestoneId === OwnershipMilestoneId.A_S4_END_PRO) {
      return 'Acquired';
    }
    if (this.milestoneId === OwnershipMilestoneId.D_S4_END_PRO) {
      return 'Divested';
    } else return null;
  }

  isPlausibilityWarning(): boolean {
    if (this.isNotAvailable() || this.readonly) {
      return false;
    }
    const date = this.getDate();
    if (!date) {
      return false;
    }
    const value: number = date.getTime();
    const now: number = Date.now();
    if (this.isComplete()) {
      return now < value;
    }
    return now > value;
  }

  getPlausibilityWarningTooltip(): string {
    if (this.isComplete()) {
      return (
        'PLAUSIBILITY WARNING: Please check this date as it is a completed date in the future. ' +
        'If this has not yet been completed remove the tick from the Complete box otherwise change the date to when the milestone was completed in the past.'
      );
    }
    return (
      'PLAUSIBILITY WARNING: Please check this date as it is an uncompleted date in the past. ' +
      'If this has now been completed place a tick in the Complete box otherwise re-forecast the date into the future.'
    );
  }
}
